import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useHistory, useParams } from 'react-router';
import { FormattedDate, FormattedTime } from 'react-intl';
import { useTranslation } from 'react-i18next';
import {
    useFetchTopicQuery,
    useFetchSessionQuery,
    CircleRSVPQuestionAnswerInput,
    useCreateAuthenticatedRsvpMutation,
} from '@modernhealth/circles-graphql/v2';
import { layout } from '@modernhealth/vital-foundation';
import {
    ButtonGroup,
    Button,
    ComponentTextXSmall,
    HeadingText50,
    renderMessage,
    HeadingText40,
    BodyTextMedium,
    Checkbox,
    BodyTextSmall,
} from '@modernhealth/vital-components';
import { formatCircleBackgroundStyle } from '@modernhealth/circles-web-components';
import { QuestionAnswers, QuestionAnswer } from '@modernhealth/circles-graphql/dist/types/types';
import { filterNullish } from '@modernhealth/filter-nullish';
import { CircleTopicFormat } from '@modernhealth/circles-graphql';

import { captureException } from 'src/shared/helpers/captureException';
import { InternalLinkButton } from 'src/shared/components/InternalLinkButton';
import { InternalLink } from 'src/shared/components/InternalLink';
import { useUpdateAccountSharedPreferencesMutation } from 'src/modules/scheduledContent/documents/updateAccountSharedPreferencesMutation.generated';
import { GetMemberInformationQueryDocument } from 'src/modules/user/documents/getMemberInformationQuery.generated';
import { useSharedPreferences } from 'src/shared/helpers/useSharedPreferences';
import { SharedPreferences } from 'src/shared/models/shared';

import { getErrorMessage } from '../utils/getErrorMessage';
import { TranslatedCircleFormat } from '../components/TranslatedCircleFormat';
import { BackButton } from '../components/BackButton';
import TopicPageSkeleton from '../components/TopicPageSkeleton';
import { InlineBulletList } from '../components/InlineBulletList';
import { RsvpQuestions } from '../components/RsvpQuestions';

interface RsvpProps {
    path?: string;
    sessionId: string;
    topicSlug: string;
}

interface TopicTypeTextProps {
    textColor: string;
}

const CheckboxContainer = styled.div({
    marginBottom: '24px',
    marginTop: '24px',
    width: '100%',
});

const TopicTypeText = styled(ComponentTextXSmall)<TopicTypeTextProps>(({ textColor }) => ({
    color: textColor,
    textTransform: 'uppercase',
    fontSize: '16px',
}));

const Wrapper = styled.div({
    width: '100%',
    maxWidth: '560px',
    margin: '0 auto',
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    paddingBottom: layout.footerHeight,
});

const TopicInfoCard = ({
    name,
    format,
    startTime,
}: {
    name: string;
    format: CircleTopicFormat;
    startTime: string;
}) => {
    const { textColor } = formatCircleBackgroundStyle(format);

    return (
        <div css={{ marginBottom: '24px' }}>
            <HeadingText50>{name}</HeadingText50>
            <div
                css={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 12,
                }}
            >
                <TopicTypeText css={{ marginBottom: 4 }} textColor={textColor}>
                    <TranslatedCircleFormat format={format} />
                </TopicTypeText>

                <InlineBulletList>
                    <FormattedDate day="numeric" month="short" weekday="short" value={startTime} />
                    <FormattedTime
                        hour="numeric"
                        minute="numeric"
                        timeZoneName="short"
                        value={startTime}
                    />
                </InlineBulletList>
            </div>
        </div>
    );
};

export const CirclesRsvpPage = () => {
    const { sessionId, topicSlug } = useParams<RsvpProps>();
    const { t } = useTranslation('circles');
    const history = useHistory();
    const [questionAnswers, setQuestionAnswers] = React.useState<QuestionAnswers>({});
    const [guidelinesAgreed, setGuidelinesAgreed] = React.useState(false);
    const [createRsvp, { error: rsvpError, loading: rsvpLoading }] =
        useCreateAuthenticatedRsvpMutation();

    const [updateAccountSharedPreferencesMutation] = useUpdateAccountSharedPreferencesMutation({
        refetchQueries: [
            {
                query: GetMemberInformationQueryDocument,
            },
        ],
    });
    const [showErrors, setShowErrors] = React.useState(false);
    const { data, loading, error, refetch } = useFetchTopicQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            topicSlug,
        },
    });

    const {
        data: sessionData,
        loading: sessionLoading,
        error: sessionError,
    } = useFetchSessionQuery({
        variables: {
            sessionId,
        },
    });

    useEffect(() => {
        if (error || sessionError || rsvpError) {
            const errorToReport = error || sessionError || rsvpError;
            captureException(errorToReport);
            renderMessage({
                content: t(getErrorMessage()),
                type: 'error',
                duration: 5,
            });
        }
    }, [error, history, rsvpError, sessionError, t]);

    const session = sessionData?.circleSession;
    const topic = data?.circleTopicBySlug;
    const firstTimeToRsvp = !useSharedPreferences(SharedPreferences.CirclesAgreedToGuidelines);
    useEffect(() => {
        if (!session && !sessionLoading && !sessionError) {
            captureException(new Error('Missing session data needed to register'));
        }
    }, [session, sessionLoading, sessionError]);

    useEffect(() => {
        if (!topic && !loading && !error) {
            captureException(new Error('Missing session data needed to register'));
        }
    }, [topic, loading, error]);

    if ((loading && !topic) || (sessionLoading && !session)) {
        return (
            <Wrapper>
                <TopicPageSkeleton />;
            </Wrapper>
        );
    }

    if (!topic || !session) {
        return (
            <div>
                Failed to load session. <Button onClick={() => refetch()}>Retry?</Button>
            </div>
        );
    }
    const startTime = session.startTime;
    const questions = filterNullish(topic.rsvpQuestions ?? []);

    if (questions.length < 1 && !firstTimeToRsvp) {
        history.push(`/circles/${topicSlug}`);
        return null;
    }
    const setQuestionAnswer = ({ id, answerText, answerId }: QuestionAnswer) => {
        setQuestionAnswers({ ...questionAnswers, [id]: { answerId, answerText } });
    };

    const isQuestionaireValid = () => {
        // setting blank answers for required questions
        const requiredQuestions = questions?.filter(question => question.required);
        const requiredQuestionIds = requiredQuestions.map(question => question.id);
        const allRequiredAnswersArePresent = requiredQuestionIds.every(
            requiredQuestionId =>
                requiredQuestionId in questionAnswers &&
                (!!questionAnswers[requiredQuestionId].answerText ||
                    !!questionAnswers[requiredQuestionId].answerId),
        );
        const otherRequirementsCompleted = firstTimeToRsvp ? guidelinesAgreed : true;
        return allRequiredAnswersArePresent && otherRequirementsCompleted;
    };

    const formatAnswers = (answers: QuestionAnswers): CircleRSVPQuestionAnswerInput[] => {
        const answersEntries = Object.entries(answers);
        const formattedAnswers = answersEntries.map(([questionId, { answerId, answerText }]) => ({
            questionId,
            answerId,
            answerText: answerText?.toString(),
        }));
        return formattedAnswers;
    };

    const addRsvp = async (
        sessionId: string,
        rsvpQuestionAnswers: CircleRSVPQuestionAnswerInput[],
    ) => {
        const { data } = await createRsvp({
            variables: {
                data: {
                    sessionId,
                    rsvpQuestionAnswers,
                },
            },
        });

        const rsvp = data?.circleCreateAuthenticatedRsvp;

        if (rsvp && 'rsvpId' in rsvp) {
            history.push(`/circles/${topicSlug}/registered/${rsvp.rsvpId}/confirmation`);
        } else {
            const errorType = data?.circleCreateAuthenticatedRsvp?.__typename;
            renderMessage({
                content: t(getErrorMessage(errorType)),
                type: 'error',
                duration: 5,
            });
        }
    };

    const agreeToCommunityGuidelines = async () => {
        return await updateAccountSharedPreferencesMutation({
            variables: {
                key: SharedPreferences.CirclesAgreedToGuidelines,
                value: 'true',
            },
        });
    };

    const errorText = t('registration_errors.checkbox_confirm_statement');
    return (
        <Wrapper>
            <div css={{ marginBottom: '20px' }}>
                <BackButton />
            </div>
            <TopicInfoCard
                name={session.topic.name}
                format={session.topic.format}
                startTime={startTime}
            />
            <RsvpQuestions
                questions={questions}
                questionAnswers={questionAnswers}
                setQuestionAnswer={setQuestionAnswer}
                showErrors={showErrors}
                note={topic.note}
            />
            {firstTimeToRsvp && (
                <section>
                    <HeadingText40> {t('session.rsvp.terms_and_conditions')}</HeadingText40>
                    <BodyTextMedium>
                        {t('session.rsvp.terms_and_conditions_clarification')}
                    </BodyTextMedium>
                    <InternalLink to="/circles/guidelines">
                        <BodyTextMedium>{t('session.rsvp.community_guidelines')}</BodyTextMedium>
                    </InternalLink>

                    <CheckboxContainer>
                        <Checkbox
                            label={t('session.rsvp.community_guidelines.agree')}
                            onChange={() => setGuidelinesAgreed(!guidelinesAgreed)}
                            value={guidelinesAgreed}
                        />
                        {showErrors && (
                            <BodyTextSmall
                                css={{ paddingLeft: '8px', paddingTop: '8px' }}
                                fontColor="red55"
                            >
                                {errorText}
                            </BodyTextSmall>
                        )}
                    </CheckboxContainer>
                </section>
            )}
            <ButtonGroup>
                <InternalLinkButton variant="secondary" size="medium" to={`/circles/${topicSlug}`}>
                    {t('pages.registration_form.exit_button.title')}
                </InternalLinkButton>
                <Button
                    variant="primary"
                    disabled={rsvpLoading}
                    size="medium"
                    onClick={() => {
                        if (!isQuestionaireValid()) {
                            setShowErrors(true);
                            return;
                        }

                        const rsvpQuestionAnswers = formatAnswers(questionAnswers);

                        addRsvp(sessionId, rsvpQuestionAnswers)
                            .then(() => agreeToCommunityGuidelines())
                            .catch(error => {
                                captureException(error);
                                renderMessage({
                                    content: t(getErrorMessage()),
                                    type: 'error',
                                    duration: 5,
                                });
                            });
                    }}
                >
                    {t('pages.registration_form.register_button.title')}
                </Button>
            </ButtonGroup>
        </Wrapper>
    );
};
